import {AsyncPipe} from '@angular/common';
import {AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {A11yModule} from '@angular/cdk/a11y';
import {CookieService} from '@app/services/cookie.service';
import {DcpCookiePreferencesService} from '@app/services/cookie-preferences/dcp-cookie-preferences.service';
import {GeolocationService} from '@app/services/geolocation.service';
import {CLOSE_EVENT, STORE_LOADED_EVENT, STORE_RESIZE_EVENT} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BaseSiteService} from '@spartacus/core';
import {first} from 'rxjs/operators';
import {ScriptInjectionService} from './config/script-injection.service';
import {GoogleAutocompleteService} from './services/google-autocomplete.service';
import {IFrameMessageService} from './services/iframe/iframe-message.service';
import {ProductDetailsPageHistoryService} from './services/user-registration/product-details-page-history.service';
import {DcpStorefrontComponent} from './layout/dcp-storefront/dcp-storefront.component';
import {DefaultModalContainerComponent} from './shared/modal/default-modal-container/default-modal-container.component';
import {DcpSupportFormHostComponent} from './shared/support-form/host/dcp-support-form-host.component';

@UntilDestroy()
@Component({
  selector: 'dcp-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [AsyncPipe, A11yModule, DcpStorefrontComponent, DefaultModalContainerComponent, DcpSupportFormHostComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private geolocationService = inject(GeolocationService);
  private cookieService = inject(CookieService);
  private cookiePreferencesService = inject(DcpCookiePreferencesService);

  title = 'dcp-commerce-ui';
  modalMode: boolean = false;
  modalIsSlim: boolean = false;
  modalHeader: string;
  @ViewChild('wrapper') private wrapper!: ElementRef;

  resizeObserver: null | ResizeObserver;

  constructor(
    public baseSiteService: BaseSiteService,
    public changeDetectorRef: ChangeDetectorRef,
    private messageService: IFrameMessageService,
    private scriptInjectionService: ScriptInjectionService,
    public googleAutocompleteService: GoogleAutocompleteService,
    public productDetailsPageHistoryService: ProductDetailsPageHistoryService,
  ) {}

  @HostListener('click')
  closeModal() {
    if (this.modalMode) {
      this.messageService.postMessage({
        eventId: CLOSE_EVENT,
      });
    }
  }

  /**
   * Opens the cookie-preferences dialog on the `openCookiePreferences` document
   * event (dispatched e.g. by Joule's "View settings"). Bound on `dcp-root` —
   * mounted in both the host document and the configurator iframe — so it fires
   * even where the footer (`@if (!isModal)`) is absent.
   */
  @HostListener('document:openCookiePreferences')
  onOpenCookiePreferences(): void {
    this.cookiePreferencesService.open();
  }

  ngOnInit(): void {
    this.getGeolocationDataOfCurrentUserSession();

    this.scriptInjectionService.injectExternalScripts();

    this.handleToModalProperties();
  }

  ngAfterViewInit(): void {
    this.setUpResizeObserver();
    this.messageService.postMessage({
      eventId: STORE_LOADED_EVENT,
    });
  }

  getGeolocationDataOfCurrentUserSession(): void {
    this.geolocationService
      .getSessionGeolocation()
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          this.cookieService.setCookie('dcp_session_city', headers.get('Ew_city'), 7);
          this.cookieService.setCookie('dcp_session_country', headers.get('Ew_country'), 7);
          this.cookieService.setCookie('dcp_session_region', headers.get('Ew_region'), 7);
        },
        error: (errorMessage) => {
          throw new Error(errorMessage);
        },
      });
  }

  setUpResizeObserver(): void {
    if (this.messageService.isIframe()) {
      this.resizeObserver = new ResizeObserver((_) => {
        this.onResize();
      });
      this.resizeObserver.observe(this.wrapper.nativeElement);
    }
  }

  onResize() {
    const element = this.wrapper.nativeElement;
    const {offsetHeight, scrollHeight, offsetWidth, scrollWidth} = element;
    this.messageService.postMessage({
      eventId: STORE_RESIZE_EVENT,
      details: {
        offsetHeight,
        scrollHeight,
        offsetWidth,
        scrollWidth,
      },
    });
  }

  ngOnDestroy(): void {
    if (this.messageService.isIframe()) {
      this.resizeObserver.unobserve(this.wrapper.nativeElement);
    }
  }

  /**
   * The entire DCP page might be displayed as a modal.
   * This is used in SAP4Me, when the configurator is opened.
   */
  handleToModalProperties() {
    this.messageService
      .getActiveModalMode()
      .pipe(untilDestroyed(this))
      .subscribe((mode) => {
        this.modalIsSlim = mode === 'slimModal';
        this.modalMode = this.modalIsSlim || mode === 'modal';
      });
  }

  isModalMode(): boolean {
    return this.modalMode;
  }

  isSlimMode(): boolean {
    return this.isModalMode() && this.modalIsSlim;
  }

  /**
   * Click event stop propagation was added in https://github.tools.sap/DCP/dcp-commerce-ui/pull/665/files#diff-8d554c98f4c32fee762d6a18e8e189f8898124312ba390bdc51fadd38345674fR1
   * This makes contextual navigation stop working properly - so stopping propagation for
   * modal mode only if not clicking the cx navigation.
   *
   * @param event
   */
  stopClickPropagation(event: Event) {
    if (this.isModalMode()) {
      event.stopPropagation();
    }
  }
}
